// src/cmps/UserProfile/UserWatchlist/WatchlistSearch.jsx
import { useState } from 'react'
import { postListImages } from '../../../services/images.service'
import { getAnimeImage } from '../../../services/util.service'

export function WatchlistSearch({
  statuses,
  currentStatusToAdd,
  searchTerm,
  setSearchTerm,
  searchResults,
  handleSearch,
  handleAddItem,
  watchlist,
}) {
  /* סטייט לבחירת אנימות */
  const [selectedAnimeIds, setSelectedAnimeIds] = useState([])

  /* בדיקה אם אנימה כבר קיימת ברשימה */
  const isInWatchlist = (mal_id) =>
    watchlist.some(
      (item) => item.anizone_id === mal_id || item.mal_id === mal_id
    )

  /* סימון / ביטול סימון */
  const toggleSelection = (id) => {
    setSelectedAnimeIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    )
  }

  /* סימון כל הפריטים האפשריים */
  const selectAll = () => {
    const selectable = searchResults.filter((item) => !isInWatchlist(item.mal_id))
    const selectableIds = selectable.map((item) => item.mal_id)

    const allSelected = selectableIds.every((id) => selectedAnimeIds.includes(id))

    setSelectedAnimeIds((prev) =>
      allSelected
        ? prev.filter((id) => !selectableIds.includes(id)) // בטל סימון
        : [...prev, ...selectableIds.filter((id) => !prev.includes(id))]
    )
  }

  /* הוספת כל האנימות המסומנות */
  const addSelectedAnimes = () => {
    selectedAnimeIds.forEach((id) => {
      const anime = searchResults.find((item) => item.mal_id === id)
      if (anime && !isInWatchlist(id)) {
        handleAddItem(currentStatusToAdd, anime)
      }
    })
    setSelectedAnimeIds([])
  }

  const currentLabel =
    statuses.find((s) => s.value === currentStatusToAdd)?.label || ''

  /* --- JSX --- */
  return (
    <div className="mb-[100px]">
      {/* כותרת + חיפוש */}
      <div className="text-center mb-4">
        <h3 className="text-xl text-white">
          בחר פריטים להוספה ל‑
          <span className="text-primary">{currentLabel}</span>
        </h3>

        <form
          onSubmit={handleSearch}
          className="flex flex-col md:flex-row items-center justify-center gap-2 mt-4 p-2"
        >
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="חפש אנימה..."
            className="p-2 w-full md:w-64 border text-white bg-transparent border-gray-400 rounded focus:outline-none"
          />

          <button type="submit" className="relative flex items-center">
            <img src={postListImages.searchBtn} alt="חיפוש" />
            <span className="absolute inset-0 flex items-center justify-center text-xs">
              חיפוש
            </span>
          </button>

          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="p-2 bg-red-500 text-white rounded"
            >
              נקה
            </button>
          )}
        </form>
      </div>

      {/* פעולות מרובות */}
      {searchResults.length > 0 && (
        <div className="flex justify-between items-center mb-4 px-4">
          <button
            onClick={selectAll}
            className="text-sm text-primary hover:text-[#970020]"
          >
            {searchResults
              .filter((item) => !isInWatchlist(item.mal_id))
              .every((item) => selectedAnimeIds.includes(item.mal_id))
              ? 'בטל סימון הכל'
              : 'סמן הכל'}
          </button>

          {selectedAnimeIds.length > 0 && (
            <button
              onClick={addSelectedAnimes}
              className="bg-primary hover:bg-[#970020] text-white py-2 px-4 rounded-lg"
            >
              הוסף {selectedAnimeIds.length} אנימות
            </button>
          )}
        </div>
      )}

      {/* תוצאות החיפוש */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 p-4">
        {searchResults.map((item) => {
          const isSelected = selectedAnimeIds.includes(item.mal_id)
          const exists = isInWatchlist(item.mal_id)

          return (
            <li
              key={item.mal_id}
              className={`bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col h-full ${
                isSelected ? 'border-4 border-primary' : ''
              }`}
            >
              <div className="relative">
                <img
                  src={getAnimeImage(item)}
                  alt={item.title || item.title_english || 'Anime'}
                  className="w-full h-[220px] md:h-[420px] object-cover"
                  loading="lazy"
                />
              </div>

              <div className="p-4 flex flex-col flex-grow text-right">
                <h3 className="text-white font-semibold text-lg mb-2">
                  {item.title}
                </h3>

                <div className="text-sm text-gray-300 space-y-1 flex-grow">
                  <p><span className="font-medium">שם באנגלית:</span> {item.title_english || 'לא צוין'}</p>
                  <p><span className="font-medium">שם בעברית:</span> {item.title_hebrew || 'לא צוין'}</p>
                  <p><span className="font-medium">פרקים:</span> {item.episodes}</p>
                  <p><span className="font-medium">סטטוס שידור:</span> {item.status}</p>
                  <p><span className="font-medium">תאריך התחלה:</span> {item.aired?.from
                    ? new Intl.DateTimeFormat('he-IL', { timeZone: 'Asia/Jerusalem' }).format(new Date(item.aired.from))
                    : 'לא ידוע'}
                  </p>
                  <p><span className="font-medium">סוג:</span> {item.type}</p>
                </div>

                <div className="flex gap-2 items-center mt-4">
                  {exists ? (
                    <span className="text-sm text-green-500">
                      האנימה כבר ברשימה
                    </span>
                  ) : (
                    <>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(item.mal_id)}
                        className="w-5 h-5"
                      />
                      <span className="text-sm text-white">סמן להוספה</span>
                    </>
                  )}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
